import { Component, Input, signal, WritableSignal } from '@angular/core';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { PlIcon } from '@app/icon-library/icons';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { FriendSurveyStatus } from '@app/profile/friendsSurveyStatus/friendsSurveyStatus.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { I18nService } from '@app/i18n';

export enum FormControlName {
  name = 'name',
  email = 'email',
}

interface EditFriendDetailsForm {
  [FormControlName.name]: FormControl<string>;
  [FormControlName.email]: FormControl<string>;
}

@Component({
  selector: 'pl-profile-single-friend-status',
  templateUrl: './singleFriendStatus.component.html',
})
export class SingleFriendStatusComponent {
  @Input({ required: true }) public status!: FriendSurveyStatus;
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly FormControlName: typeof FormControlName = FormControlName;
  public readonly PlButtonType: typeof PlButtonType = PlButtonType;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  public showForm: WritableSignal<boolean> = signal(false);
  public nudgeSent: WritableSignal<boolean> = signal(false);
  public error: WritableSignal<string | null> = signal(null);

  public form!: FormGroup<EditFriendDetailsForm>;

  constructor(
    private httpService: HttpService,
    private i18nService: I18nService,
  ) {
    this.createForm();
  }

  public createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<EditFriendDetailsForm>({
      [FormControlName.name]: fb.nonNullable.control('', [Validators.required]),
      [FormControlName.email]: fb.nonNullable.control('', [Validators.required]),
    });
  }

  public showFormToEditFriendDetails(): void {
    this.form.controls[FormControlName.name].setValue(`${this.status.first_name} ${this.status.last_name}`);
    this.form.controls[FormControlName.email].setValue(this.status.email);
    this.showForm.set(true);
  }

  public nudge(): void {
    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.POST,
      url: `/360Survey/${this.status.id}/resend`,
    };

    this.httpService.request(requestOptions).subscribe({
      next: (): void => {
        this.nudgeSent.set(true);
        this.error.set(null);
      },
      error: (): void => {
        this.error.set(this.i18nService.translate('login.errors.unexpected_error'));
      },
    });
  }

  public discardChangeEditFriendDetails(): void {
    this.showForm.set(false);
  }

  public saveFriendsDetails(): void {
    const fullName: string = this.form.controls[FormControlName.name].value;
    const email: string = this.form.controls[FormControlName.email].value;

    const [firstName, lastName]: string[] = fullName.split(/\s+(.*)/);

    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.PUT,
      url: `/360Survey/${this.status.id}`,
      body: {
        email,
        first_name: firstName || '',
        id: this.status.id,
        last_name: lastName || '',
      },
    };

    this.httpService.request(requestOptions).subscribe({
      next: (singleFriendSurveyStatus: FriendSurveyStatus): void => {
        this.status = singleFriendSurveyStatus;
        this.showForm.set(false);
        this.error.set(null);
      },
      error: (): void => {
        this.error.set(this.i18nService.translate('login.errors.unexpected_error'));
      },
    });
  }
}
